import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFlask,
  faChartLine,
  faCalculator,
  faRulerCombined,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import PublicNavBar from '../../components/layout/PublicNavBar';
import PublicFooter from '../../components/layout/PublicFooter';
import { usePageMeta } from '../../hooks/usePageMeta';

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return {
    ref,
    className: `transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`,
  };
}

interface LabTool {
  to: string;
  title: string;
  description: string;
  icon: IconDefinition;
  tags: string[];
}

const tools: LabTool[] = [
  {
    to: '/labs/tradingview-indicator-tdr',
    title: 'TDR Indicator',
    description:
      'Free TradingView indicator that layers structured signals, performance metrics, and risk zones directly onto your charts.',
    icon: faChartLine,
    tags: ['TradingView', 'Signals', 'Free'],
  },
  {
    to: '/labs/strategy-performance-calculator',
    title: 'Strategy Performance Calculator',
    description:
      'Compute expectancy, Kelly criterion, probability of ruin, and run 1,000 Monte Carlo simulations from your win rate and R:R.',
    icon: faCalculator,
    tags: ['Expectancy', 'Kelly', 'Monte Carlo'],
  },
  {
    to: '/labs/position-size-calculator',
    title: 'Position Size Calculator',
    description:
      'Calculate the correct position size for any risk level from your account size, entry price, and stop-loss.',
    icon: faRulerCombined,
    tags: ['Risk Management', 'Position Sizing', 'Free'],
  },
];

export default function LabsIndexPage() {
  usePageMeta({
    title: 'Labs',
    description:
      'Free trading tools and calculators from Quanthop. Strategy performance calculator, position size calculator, and the TDR TradingView indicator.',
  });

  const revHero = useScrollReveal(0.1);
  const revCards = useScrollReveal(0.1);

  return (
    <div className="min-h-screen bg-[#0a0e14] text-gray-100 font-[Poppins] selection:bg-[#9d4edd]/30 flex flex-col">
      <PublicNavBar />

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section
        ref={revHero.ref}
        className={`relative overflow-hidden pt-32 pb-16 px-4 sm:px-6 ${revHero.className}`}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1520] via-[#0a0e14] to-[#080b11]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] rounded-full bg-[#9d4edd]/[0.035] blur-[160px] pointer-events-none" />

        <div className="relative max-w-4xl mx-auto text-center space-y-5">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#9d4edd]/10 border border-[#9d4edd]/20 text-[#c77dff] text-xs font-medium">
            <FontAwesomeIcon icon={faFlask} className="text-[10px]" />
            Free Tools
          </div>
          <h1 className="text-3xl md:text-[42px] font-bold text-white leading-tight tracking-[-0.02em]">
            Quanthop Labs
          </h1>
          <p className="text-gray-400/85 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Open-access calculators and indicators built to sharpen your
            research workflow — no signup required.
          </p>
        </div>
      </section>

      {/* ── Tool Cards ───────────────────────────────────────────────── */}
      <section
        ref={revCards.ref}
        className={`flex-1 px-4 sm:px-6 pb-24 ${revCards.className}`}
      >
        <div className="max-w-5xl mx-auto grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {tools.map((tool) => (
            <Link
              key={tool.to}
              to={tool.to}
              className="group flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] hover:border-[#9d4edd]/25 hover:bg-white/[0.04] transition-all duration-300 p-6"
            >
              <div className="w-10 h-10 rounded-lg bg-[#9d4edd]/10 flex items-center justify-center mb-4">
                <FontAwesomeIcon
                  icon={tool.icon}
                  className="text-[#c77dff] text-sm"
                />
              </div>

              <h2 className="text-lg font-semibold text-white mb-2">
                {tool.title}
              </h2>

              <p className="text-sm text-gray-400/85 leading-relaxed mb-4 flex-1">
                {tool.description}
              </p>

              <div className="flex flex-wrap gap-2 mb-5">
                {tool.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-white/[0.05] text-gray-400/70 border border-white/[0.04]"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <span className="inline-flex items-center gap-1.5 text-[13px] text-[#c77dff] group-hover:gap-2.5 transition-all duration-200">
                Open tool
                <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
